import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Box from "../../../components/Box";
import Table from "../../../components/Table";
import Button from "../../../components/Button";
import ButtonDropdown from "../../../components/ButtonDropdown";
import FilterSimple from "../../../components/FilterSimple";
import Pagination from "../../../components/Pagination";
import Icon from "../../../components/Icon";
import ModalFilter from "./ModalFilter";
import DocumentStatus from "../components/DocumentStatus";
import ModalDocumentProblem from "../components/ModalDocumentProblem";
import ModalCancelDocument from "../components/ModalCancelDocument";
import ModalCorrectDocument from "../components/ModalCorrectDocument";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { getToken, hasPermission } from "../../../services/auth";
import { getSubdomain } from "../../../helpers/domain";
import { convertIntToMoney } from "../../../helpers/conversions";
import { getModelLabel, isPending } from "../helpers";
import { parseBlobError, printDocument, printDocuments } from "../print";
import { subscribeFiscalDocuments } from "../realtime";

// Busca rápida: o campo escolhido vai para a URL e o backend procura só
// nele. O nome do destinatário vem primeiro por ser o padrão — é por quem
// recebeu a nota que se procura no dia a dia.
const searchParams = [
    { name: 'Nome do destinatário', key: 'receiver_name', operation: 'contain', type: 'text' },
    { name: 'CNPJ / CPF do destinatário', key: 'receiver_document', operation: 'contain', type: 'text' },
    { name: 'Número da nota', key: 'number', operation: 'contain', type: 'text' },
    { name: 'Pedido', key: 'order_code', operation: 'contain', type: 'text' },
    { name: 'Chave de acesso', key: 'access_key', operation: 'contain', type: 'text' },
];

const DEFAULT_SEARCH_FIELD = searchParams[0].key;

// Filtros que vivem no modal — a busca do topo fica de fora da contagem.
const MODAL_FILTER_KEYS = ['status', 'model', 'companyId', 'startDate', 'endDate'];

// O desfecho da nota chega por webhook e é empurrado por websocket, em
// segundos. Passou de 1 minuto, o retorno pode ter se perdido — a partir
// daqui a linha ganha o botão de atualizar, que reconsulta o provedor
// (decisão de 15/08: sem retentativa automática, sem reconciliação e sem
// polling; o caminho manual é a rede de segurança).
const STUCK_AFTER_MINUTES = 1;

// Trava tanto a nota que não teve desfecho quanto a CARTA DE CORREÇÃO
// esperando resposta: a nota segue "autorizada" enquanto a carta processa,
// então sem olhar o evento o botão nunca apareceria para ela.
const isStuck = doc => {
    const waitingSince = isPending(doc.status) ? doc.updated_at : doc.pending_event_at;

    return !!waitingSince
        && moment().diff(moment(waitingSince), 'minutes') >= STUCK_AFTER_MINUTES;
};

const normalizePage = page => {
    const parsed = parseInt(page, 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const parseFilters = search => {
    const params = new URLSearchParams(search);

    return {
        page: normalizePage(params.get('page')),
        search: params.get('search') || '',
        searchField: params.get('search_field') || DEFAULT_SEARCH_FIELD,
        status: params.get('status') || '',
        model: params.get('model') || '',
        companyId: params.get('company_id') || '',
        startDate: params.get('start_date') || '',
        endDate: params.get('end_date') || '',
    };
};

const countActiveFilters = filters =>
    MODAL_FILTER_KEYS.filter(key => filters[key] !== '' && filters[key] !== null).length;

const buildApiQuery = filters => {
    const params = new URLSearchParams();

    if (filters.search.trim() !== '') {
        params.set('search', filters.search.trim());
        if (filters.searchField) params.set('search_field', filters.searchField);
    }
    if (filters.status) params.set('status', filters.status);
    if (filters.model) params.set('model', filters.model);
    if (filters.companyId) params.set('company_id', filters.companyId);
    if (filters.startDate) params.set('start_date', filters.startDate);
    if (filters.endDate) params.set('end_date', filters.endDate);
    params.set('page', filters.page);

    return params.toString();
};

const buildListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.search.trim() !== '') {
        params.set('search', filters.search.trim());
        if (filters.searchField) params.set('search_field', filters.searchField);
    }
    if (filters.status) params.set('status', filters.status);
    if (filters.model) params.set('model', filters.model);
    if (filters.companyId) params.set('company_id', filters.companyId);
    if (filters.startDate) params.set('start_date', filters.startDate);
    if (filters.endDate) params.set('end_date', filters.endDate);

    return params.toString();
};

/** Todas as notas emitidas pelo tenant (BDM-887). */
export const FiscalDocumentList = () => {
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [data, setData] = useState({ header: [], rows: [] });
    const [issuers, setIssuers] = useState([]);
    const [showFilter, setShowFilter] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    // Seleção que passou da página: guarda também o que o backend disse que
    // dá para enviar/imprimir, porque essas notas não estão à vista e a
    // regra de "pode" nunca é recalculada aqui.
    const [allResults, setAllResults] = useState(null);
    const [loadingAllIds, setLoadingAllIds] = useState(false);
    // O getDocuments é chamado por callbacks nascidos em outro render (o
    // websocket, o timer): o estado que ele lê tem que vir de um ref, senão
    // ele decide com um valor velho e apaga a seleção que acabou de ser feita.
    const allResultsRef = useRef(null);
    const selectAllResults = value => {
        allResultsRef.current = value;
        setAllResults(value);
    };
    const [sending, setSending] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [refreshingId, setRefreshingId] = useState(null);
    // Ciclo de vida da nota autorizada: correção e cancelamento saem daqui,
    // com os mesmos modais que a aba do pedido usa.
    const [documentToCancel, setDocumentToCancel] = useState(null);
    const [documentToCorrect, setDocumentToCorrect] = useState(null);
    const [documentWithProblem, setDocumentWithProblem] = useState(null);
    // Relógio local (sem rede): com o polling aposentado, é ele que
    // re-renderiza a lista para o botão "Atualizar" aparecer quando o minuto passa.
    const [clock, setClock] = useState(0);
    const refetchTimer = useRef(null);
    // O callback do websocket nasce no mount, mas o getDocuments captura os
    // filtros da URL do momento — sem o ref, o evento recarregaria a lista
    // com filtros velhos.
    const getDocumentsRef = useRef(() => {});

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const filters = useMemo(() => parseFilters(location.search), [location.search]);
    const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);
    const canManage = hasPermission('manage_fiscal');

    // As ações em massa valem só para o que faz sentido em cada uma: enviar
    // pede nota pendente; imprimir pede nota já emitida. Com "todos os
    // resultados" a seleção passa da página, então as capacidades vêm de
    // duas fontes: as linhas à vista e a resposta do /ids.
    const capableIds = (pageFlag, allKey) => {
        const allowed = new Set([
            ...documents.filter(doc => doc[pageFlag]).map(doc => doc.id),
            ...(allResults?.[allKey] ?? []),
        ]);

        return selectedIds.filter(id => allowed.has(id));
    };

    const sendableSelectedIds = useMemo(
        () => capableIds('can_send', 'sendable_ids'),
        [documents, selectedIds, allResults]
    );
    const printableSelectedIds = useMemo(
        () => capableIds('has_files', 'printable_ids'),
        [documents, selectedIds, allResults]
    );

    useEffect(() => {
        getIssuers();
    }, []);

    useEffect(() => {
        getDocumentsRef.current = getDocuments;
    });

    // Tempo real: nota que muda de situação recarrega a lista em silêncio.
    // Vários eventos juntos (envio em massa) viram UMA recarga (debounce).
    // Não há mais polling de dados (decisão de 15/08): se o websocket ou o
    // webhook falharem, a saída é o botão "Atualizar" da linha presa.
    useEffect(() => {
        const unsubscribe = subscribeFiscalDocuments(
            () => {
                clearTimeout(refetchTimer.current);
                refetchTimer.current = setTimeout(() => getDocumentsRef.current(true), 400);
            },
            // Reconectou: re-sincroniza, porque o canal não reenvia o que
            // aconteceu durante a queda.
            () => getDocumentsRef.current(true)
        );

        return () => {
            clearTimeout(refetchTimer.current);
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, []);

    useEffect(() => {
        // Mudou o filtro, mudou o conjunto: uma seleção de "todos os
        // resultados" do filtro anterior não vale mais.
        selectAllResults(null);
        getDocuments();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [documents, selectedIds, refreshingId, clock]);

    // Enquanto houver nota pendente na tela, um tick de 15s re-renderiza a
    // lista SEM tocar na rede — é o que faz o "Atualizar" da linha presa
    // aparecer logo depois de o minuto passar (com tick maior, um botão
    // "de 1 minuto" só apareceria perto do minuto e meio).
    useEffect(() => {
        if (!documents.some(doc => isPending(doc.status) || doc.pending_event_at)) {
            return undefined;
        }

        const timer = setInterval(() => setClock(current => current + 1), 15000);

        return () => clearInterval(timer);
    }, [documents]);

    // Empresas emitentes: só para o filtro, e só aparece com mais de uma.
    const getIssuers = () => {
        api.get(`/fiscal-settings`).then(res => {
            setIssuers((res.data.resources ?? []).map(issuer => issuer.company));
        }).catch(() => {});
    };

    const updateUrl = (changes, resetPage = true) => {
        const next = { ...filters, ...changes, page: resetPage ? 1 : (changes.page || filters.page) };
        const search = buildListSearch(next);

        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
    };

    const getDocuments = (quiet = false) => {
        if (!quiet) {
            setDocuments([]);
            setLoading(true);
        }

        api.get(`/fiscal-document/paginate?${buildApiQuery(filters)}`).then(res => {
            setDocuments(res.data.resources.data);
            // Some da seleção o que saiu da página — agir sobre nota que
            // não está mais à vista é pedir engano. A exceção é a seleção de
            // "todos os resultados": ali estar fora da página é o ponto.
            setSelectedIds(current => allResultsRef.current
                ? current
                : current.filter(id => res.data.resources.data.some(doc => doc.id === id))
            );
            setCurrentPage(res.data.resources.current_page);
            setLastPage(res.data.resources.last_page);
            setPerPage(res.data.resources.per_page);
            setTotalResults(res.data.resources.total);
            setHasNextPage(res.data.resources.next_page_url !== null);
            setHasPrevPage(res.data.resources.prev_page_url !== null);
        }).catch(renderError).then(() => setLoading(false));
    };

    /**
     * "Selecionar todos os resultados": os ids vêm do backend, com o mesmo
     * filtro da lista. Só até o teto das ações em lote — passar disso seria
     * selecionar mil notas para depois descobrir que o lote é de 50.
     */
    const handleSelectAllResults = () => {
        if (allResults) {
            selectAllResults(null);
            setSelectedIds([]);
            return;
        }

        setLoadingAllIds(true);

        api.get(`/fiscal-document/ids?${buildApiQuery(filters)}`).then(res => {
            const resource = res.data.resource;

            selectAllResults(resource);
            setSelectedIds(resource.ids);

            if (resource.total > resource.limit) {
                toast.info(
                    `Selecionadas as ${resource.limit} notas mais recentes das ${resource.total} do filtro — ` +
                    `é o máximo que dá para enviar ou imprimir de uma vez.`
                );
            }
        }).catch(renderError).then(() => setLoadingAllIds(false));
    };

    const openFile = (id, format) => {
        window.open(`${process.env.REACT_APP_URL}/fiscal-document/${id}/${format}?_token=${getToken()}&_subdomain=${getSubdomain()}`);
    };

    /**
     * Reconsulta a nota no provedor — mesmo caminho do webhook, à mão.
     * Aparece só na linha presa há mais de 1 minuto.
     */
    const refreshDocument = id => {
        setRefreshingId(id);

        api.post(`/fiscal-document/${id}/refresh`).then(res => {
            const status = res.data.resource?.status;

            isPending(status)
                ? toast.info('O fisco ainda não respondeu — a nota continua em processamento.')
                : toast.success('Situação atualizada.');

            getDocuments(true);
        }).catch(renderError).then(() => setRefreshingId(null));
    };

    /** Envia uma nota pendente ao fisco. */
    const sendDocument = id => {
        setSending(true);

        api.post(`/fiscal-document/${id}/send`).then(() => {
            toast.success('Nota enviada. O fisco responde em segundos e a lista atualiza sozinha.');
            getDocuments(true);
        }).catch(renderError).then(() => setSending(false));
    };

    const sendSelected = () => {
        setSending(true);

        api.post(`/fiscal-document/send-batch`, { ids: sendableSelectedIds }).then(res => {
            const { sent, problems } = res.data.resource;

            if (sent > 0) {
                toast.success(`${sent} ${sent === 1 ? 'nota enviada' : 'notas enviadas'} ao fisco.`);
            }

            // Falha de uma nota não impede as outras: o que travou aparece
            // nomeado, para o usuário saber exatamente o que revisar.
            problems.forEach(problem => toast.error(
                `${problem.number ? `Nota ${problem.number}` : 'Uma das notas'}: ${problem.message}`
            ));

            selectAllResults(null);
            setSelectedIds([]);
            getDocuments(true);
        }).catch(renderError).then(() => setSending(false));
    };

    const print = async (action, ids) => {
        setPrinting(true);

        try {
            await action(ids);
        } catch (error) {
            const message = await parseBlobError(error);
            message ? toast.error(message) : renderError(error);
        }

        setPrinting(false);
    };

    const documentActions = doc => [
        {
            // Pendente ainda é rascunho e pode mudar; enviada é documento
            // fiscal e só pode ser lida.
            text: doc.can_edit ? 'Editar' : 'Visualizar',
            svg: doc.can_edit ? 'edit' : 'eye',
            action: () => navigate(`/fiscal/documents/${doc.id}`),
        },
        ...(doc.order_id ? [{
            text: 'Ver pedido',
            svg: 'order',
            action: () => navigate(`/order/edit/${doc.order_id}`),
        }] : []),
        {
            text: 'Emitir nota fiscal',
            svg: 'send',
            disabled: !doc.can_send || !canManage || sending,
            action: () => sendDocument(doc.id),
        },
        {
            text: 'Visualizar XML',
            svg: 'quote',
            disabled: !doc.has_files,
            action: () => openFile(doc.id, 'xml'),
        },
        {
            // Abre em outra aba — o ícone diz isso, e assim não se confunde
            // com o "Visualizar" da nota aqui dentro do sistema.
            text: 'Visualizar PDF',
            svg: 'external-link',
            disabled: !doc.has_files,
            action: () => openFile(doc.id, 'pdf'),
        },
        {
            text: 'Imprimir nota fiscal',
            svg: 'printer',
            disabled: !doc.has_files || printing,
            action: () => print(printDocument, doc.id),
        },
        {
            // A devolução nasce da nota que ela devolve: itens, cliente e
            // referências vêm de lá, sem redigitação.
            text: 'Gerar devolução',
            svg: 'corner-right-down',
            disabled: doc.status !== 'authorized' || doc.model !== 'nfe' || !canManage,
            action: () => navigate(`/fiscal/documents/new?return_from=${doc.id}`),
        },
        {
            // "Pode corrigir?" e "pode cancelar?" vêm do backend: além do
            // status, dependem de a nota existir no provedor — regra fiscal
            // não se recalcula no JS.
            text: doc.corrections_count > 0
                ? `Carta de correção (${doc.corrections_count})`
                : 'Carta de correção',
            svg: 'file-pen',
            disabled: !doc.can_correct || !canManage,
            action: () => setDocumentToCorrect(doc),
        },
        {
            text: 'Cancelar nota',
            svg: 'close',
            disabled: !doc.can_cancel || !canManage,
            action: () => setDocumentToCancel(doc),
        },
    ];

    const generateData = () => {
        const header = ['Emissão', 'Tipo', 'Número', 'Pedido / Cliente', 'Valor', 'Situação', ''];

        const rows = documents.map(doc => ({
            id: doc.id,
            data: [
                doc.authorized_at
                    ? moment(doc.authorized_at).format('DD/MM/YYYY')
                    : moment(doc.created_at).format('DD/MM/YYYY'),
                getModelLabel(doc.model),
                doc.number ?? '—',
                <>
                    {doc.order ? `#${doc.order.code}` : (doc.receiver_name ?? '—')}
                    {doc.created_by_automation &&
                        <span className={`finance-automation-tag`} title={`Gerada na emissão do pedido`}>auto</span>
                    }
                </>,
                convertIntToMoney(doc.amount, true),
                <div className={`fiscal-status-cell`}>
                    {/* A nota com problema só dizia "Erro" e obrigava a
                        abrir o pedido para descobrir o motivo. O texto não
                        cabe na coluna, então a etiqueta abre o detalhe. */}
                    <DocumentStatus
                        status={doc.status}
                        action={doc.friendly_problem ? () => setDocumentWithProblem(doc) : null}
                        title={doc.friendly_problem ? `Clique para ver o motivo` : null}
                    />
                    {isStuck(doc) &&
                        <button
                            className={`fiscal-status-refresh ${refreshingId === doc.id ? 'loading' : ''}`}
                            title={isPending(doc.status)
                                ? `A resposta do fisco está demorando — clique para consultar agora`
                                : `A carta de correção ainda não foi confirmada — clique para consultar agora`
                            }
                            disabled={refreshingId === doc.id}
                            onClick={event => {
                                // A linha inteira abre a nota; o clique
                                // aqui não pode ir junto.
                                event.stopPropagation();
                                refreshDocument(doc.id);
                            }}
                        >
                            <Icon name={`refresh`} size={12} className={refreshingId === doc.id ? 'spin' : ''} />
                            Atualizar
                        </button>
                    }
                </div>,
            ],
            buttons: [
                <div className={`fiscal-row-actions`}>
                    <ButtonDropdown
                        type={`secondary`}
                        size={`small`}
                        text={`Ações`}
                        actions={documentActions(doc)}
                    />
                </div>
            ],
            action: () => navigate(`/fiscal/documents/${doc.id}`),
        }));

        setData({ header, rows });
    };

    return (
        <PanelTemplate id="fiscal-document-list">
            <ModalFilter
                show={showFilter}
                close={() => setShowFilter(false)}
                filters={filters}
                setCustomFilters={changes => updateUrl(changes)}
                issuers={issuers}
            />

            <ModalDocumentProblem
                document={documentWithProblem}
                close={() => setDocumentWithProblem(null)}
            />

            <ModalCancelDocument
                document={documentToCancel}
                close={() => setDocumentToCancel(null)}
                onDone={message => {
                    toast.success(message);
                    getDocuments(true);
                }}
            />

            <ModalCorrectDocument
                document={documentToCorrect}
                close={() => setDocumentToCorrect(null)}
                onDone={message => {
                    toast.success(message);
                    getDocuments(true);
                }}
            />

            <PageHeader
                left={
                    <FilterSimple
                        params={searchParams}
                        filter={(value, metadata) => updateUrl({
                            search: metadata?.value ?? '',
                            searchField: metadata?.field || DEFAULT_SEARCH_FIELD,
                        })}
                        externalValue={filters.search}
                        externalOption={filters.searchField}
                    />
                }
                right={
                    <div className={`fiscal-list-actions`}>
                        {/* Ações em massa só aparecem com seleção — e cada
                            uma diz quantas notas realmente vai processar,
                            porque a seleção pode misturar pendentes e
                            emitidas. */}
                        {selectedIds.length > 1 &&
                            <>
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`send`}
                                    text={`Enviar ${sendableSelectedIds.length} ${sendableSelectedIds.length === 1 ? 'nota' : 'notas'}`}
                                    action={sendSelected}
                                    loading={sending}
                                    disabled={!canManage || sendableSelectedIds.length === 0}
                                />
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`printer`}
                                    text={`Imprimir ${printableSelectedIds.length} ${printableSelectedIds.length === 1 ? 'nota' : 'notas'}`}
                                    action={() => print(printDocuments, printableSelectedIds)}
                                    loading={printing}
                                    disabled={printableSelectedIds.length === 0}
                                />
                            </>
                        }
                        <Button
                            type={`secondary`}
                            size={`small`}
                            svg={`filter`}
                            text={`Filtrar`}
                            badge={activeFilterCount}
                            action={() => setShowFilter(true)}
                        />
                        <Button type={`secondary`} size={`small`} svg={`config`} text={`Configurações`} link={`/fiscal/settings`} />
                        {hasPermission('manage_fiscal') &&
                            <Button type={`primary`} size={`small`} svg={`plus-white`} text={`Nova nota`} link={`/fiscal/documents/new`} />
                        }
                    </div>
                }
            />

            <Box className={`no-padding with-pagination`}>
                <Table
                    header={data.header}
                    rows={data.rows}
                    loading={loading || loadingAllIds}
                    checks
                    selectedIds={selectedIds}
                    setSelectedIds={ids => {
                        // Mexeu na seleção à mão: deixa de ser "todos os
                        // resultados", e as capacidades voltam a sair só das
                        // linhas à vista.
                        selectAllResults(null);
                        setSelectedIds(ids);
                    }}
                    totalCount={totalResults}
                    onSelectAllResults={handleSelectAllResults}
                />
            </Box>

            {!loading &&
                <Pagination
                    currentPage={currentPage}
                    lastPage={lastPage}
                    perPage={perPage}
                    totalResults={totalResults}
                    hasNextPage={hasNextPage}
                    hasPrevPage={hasPrevPage}
                    setCurrentPage={page => updateUrl({ page }, false)}
                />
            }
        </PanelTemplate>
    );
};

export default FiscalDocumentList;
